import React, { useEffect } from 'react';
import { FormattedHTMLMessage } from 'react-intl';
import { produce } from 'immer';

import {
    VerificationStepsEnum,
    isFormFilled,
    hasFailedInstantMatch,
    setRef,
    VerificationService,
    Organization,
    MemberPersonalInfoViewModel,
    MemberPersonalInfoResponse,
    FormFieldConfig,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    HowDoesVerifyingWorkComponent as HowDoesVerifyingWork,
    LogoComponent,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    EmailComponent as Email,
    BirthDateComponent as BirthDate,
    MemberIdComponent as MemberId,
    FormFooterComponent as FormFooter,
    MarketConsentWrapper,
    CountryComponentWrapper,
    RewardsRemainingComponent,
    getOptions,
    FormFieldCustom,
    ExtraMessage,
    FieldValidationErrors,
    ErrorId,
    FieldId,
    ExtendedFieldValidationErrors,
} from '@sheerid/jslib';

import { ChangeLocaleV2Component } from '../FormFields/ChangeLocaleComponent';
import { MarinerNumber } from '../FormFields/MarinerNumber';
import { getQueryParamsByName } from '../../lib/utils/browser';

interface StepMemberPersonalInfoComponentProps {
    verificationService: VerificationService;
}

const StepMemberPersonalInfo = ({ verificationService }: StepMemberPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as MemberPersonalInfoViewModel;
    const { fieldValidationErrors }: { fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors } = verificationService;
    const verificationResponse = verificationService.verificationResponse as MemberPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const AARP_ORGANIZATION: Organization = {
        id: 4101,
        idExtended: '4101',
        name: 'AARP (Washington, DC)',
    };

    const updateMemberViewModel = (key: keyof MemberPersonalInfoViewModel, value: any) => {
        const nextState: MemberPersonalInfoViewModel = produce(
            viewModel,
            (draft: MemberPersonalInfoViewModel) => {
                (draft[key] as any) = value;
            },
        );
        verificationService.updateViewModel(nextState);
    };

    useEffect(() => {
        let marinerNumber: string = verificationService?.viewModel?.metadata?.captainsCircleNumber as string || getQueryParamsByName('marinerNumber') || '';
        if (verificationService?.previousVerificationResponse
            && verificationService?.previousVerificationResponse?.currentStep === 'docUpload') {
            // reset the marinerNumber if the user came from doc upload via max doc review failure
            marinerNumber = '';
        }
        const nextState: MemberPersonalInfoViewModel = produce(
            viewModel,
            (draft: MemberPersonalInfoViewModel) => {
                draft.metadata = { ...verificationService.viewModel.metadata, ...{ captainsCircleNumber: marinerNumber, country: verificationService.viewModel?.countryChoice?.value } };
                draft.organization = AARP_ORGANIZATION;
            },
        );
        verificationService.updateViewModel(nextState);
    }, []);

    return (
        <div id="sid-step-member-personal-info" className="sid-l-container">
            {failedInstantMatch ? (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.title"
                            defaultMessage="Check Your Name & Email"
                        />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center sid-header__subtitle--error">
                        <FormattedHTMLMessage
                            id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="Your full name and email address must match what is on record with your school."
                        />
                    </div>
                </div>
            ) : (
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.title"
                            defaultMessage="Unlock this Members-Only Offer"
                            tagName="h1"
                        />
                    </div>
                    <div className="sid-header__subtitle sid-l-horz-center">
                        <FormattedHTMLMessage
                            id="step.personalInfo.subtitle"
                            defaultMessage="Verify you're a current member of an eligible organization."
                        />
                        &nbsp;
                        <HowDoesVerifyingWork verificationService={verificationService} />
                        <RewardsRemainingComponent verificationService={verificationService} />
                    </div>
                </div>
            )}

            <ChangeLocaleV2Component verificationService={verificationService} />
            <CountryComponentWrapper verificationService={verificationService} />

            <MarinerNumber
                value={viewModel.metadata?.captainsCircleNumber}
                isErrored={!!fieldValidationErrors.captainsCircleNumber}
                errorId={fieldValidationErrors.captainsCircleNumber as ErrorId}
                onChange={(newValue: string) => {
                    updateMemberViewModel('metadata', { ...verificationService.viewModel.metadata, captainsCircleNumber: newValue });
                    updateFieldValidationErrorsByFieldId('captainsCircleNumber' as FieldId, newValue, verificationService);
                }}
            />

            <div className="sid-names">
                <FirstName
                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={(newValue) => {
                        updateMemberViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }}
                />

                <LastName
                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={(newValue) => {
                        updateMemberViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }}
                />
            </div>

            <Email
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                explanation={(
                    <FormattedHTMLMessage
                        id="emailExplanation"
                        defaultMessage="Needed to send you your unique code"
                    />
                )}
                onChange={(newValue) => {
                    updateMemberViewModel('email', newValue);
                    updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                }}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                isRequired={false}
                onChange={(newValue) => {
                    updateMemberViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <MemberId
                value={viewModel.memberId}
                isErrored={!!fieldValidationErrors.memberId}
                isRequired={false}
                onChange={(newValue) => {
                    updateMemberViewModel('memberId', newValue);
                    updateFieldValidationErrorsByFieldId('memberId', newValue, verificationService);
                }}
            />

            {verificationService.programTheme.isSmsNotifierConfigured
                || verificationService.programTheme.smsLoopEnabled ? (
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={(newValue) => {
                            updateMemberViewModel('phoneNumber', newValue);
                            updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                        }}
                        selectedCountryCode={viewModel.countryChoice && viewModel.countryChoice.value}
                    />
                ) : null}

            {getOptions().customFormFields.map((config: FormFieldConfig) => (
                <FormFieldCustom config={config} verificationService={verificationService} />
            ))}

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateMemberViewModel('metadata', {
                        ...verificationService.viewModel.metadata,
                        marketConsentValue: newValue,
                    });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <ExtraMessage verificationService={verificationService} suffix="CollectAboveSubmit" />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(
                            viewModel,
                            verificationService,
                            VerificationStepsEnum.collectMemberPersonalInfo,
                        )}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions)
                            ? 'sid-btn--disabled-like'
                            : ''
                        }`}
                        aria-labelledby="verify-status-text"
                        ref={(button) => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            {failedInstantMatch ? (
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            ) : (
                                <FormattedHTMLMessage
                                    id="verifyMyMembershipStatus"
                                    defaultMessage="Verify My Membership Status"
                                />
                            )}
                        </span>
                    </button>
                </div>
            </div>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepMemberPersonalInfoComponent = StepMemberPersonalInfo;
